import styled from 'styled-components';
import * as React from 'react';
import useAsyncEffect from 'use-async-effect';
import { useMemo, useState } from 'react';
import { Signal, useSignalEffect } from '@preact/signals-react';
import produce from 'immer';
import {
  HistoryCheckpoint,
  useAiMetadataApi,
} from '@/features/ai-agents/shared/use-ai-metadata-api';
import { getQueryParam } from '@/utils/browser';
import { groupBy } from '@/utils/arrays';
import { Spinner } from '@/Spinner';
import { capitalize } from '@/utils/types';

export interface CheckpointPopoverProps {
  firstSessionId: Signal<string>;
  lastRunId?: Signal<string>;
  onRestoreSession?: (checkpoint: HistoryCheckpoint) => void;
}

export function CheckpointPopover({
  firstSessionId,
  onRestoreSession,
  lastRunId,
}: CheckpointPopoverProps) {
  const { getSessionCheckpoints } = useAiMetadataApi(
    getQueryParam(`local-ai`) === `1`,
  );

  const [loading, setLoading] = useState(true);
  const [checkpoints, setCheckpoints] = useState<HistoryCheckpoint[]>(null);

  const checkpointsGroups = useMemo(() => {
    if (checkpoints) {
      return groupBy(checkpoints, (i) => i.dateString);
    }
    return null;
  }, [checkpoints]);

  useAsyncEffect(async () => {
    setLoading(true);
    try {
      const resp = await getSessionCheckpoints(firstSessionId.value);
      if (resp) {
        const sortedCheckpoints = resp
          ?.map((i) => {
            i.date = new Date(i.timestamp);
            i.dateString = formatDateWithOrdinal(i.date);
            i.isSelected = i.run_id === lastRunId.value;
            return i;
          })
          .sort((a, b) => b.date.getTime() - a.date.getTime());

        if (!sortedCheckpoints.find((i) => i.isSelected)) {
          sortedCheckpoints[0].isSelected = true;
        }

        setCheckpoints(sortedCheckpoints);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useSignalEffect(() => {
    if (checkpoints) {
      const changed = produce(checkpoints, (draft) => {
        draft.forEach((item) => {
          item.isSelected = item.run_id === lastRunId.value;
        });

        if (!draft.find((i) => i.isSelected)) {
          draft[0].isSelected = true;
        }
      });
      setCheckpoints(changed);
    }
  });

  return (
    <Wrapper className={loading ? `loading` : ``}>
      {loading && <Spinner size={30} border={2} />}
      {!loading &&
        (!checkpointsGroups || Object.keys(checkpoints).length === 0) && (
          <GroupName
            style={{
              textAlign: `center`,
              width: `100%`,
              height: `100%`,
              display: `flex`,
              justifyContent: `center`,
              alignItems: `center`,
              margin: `auto`,
            }}
          >
            No checkpoints available
          </GroupName>
        )}
      {!loading && checkpointsGroups && (
        <List>
          {Object.entries(checkpointsGroups)?.map(([dateString, items]) => (
            <Group key={dateString}>
              <GroupName>{dateString}</GroupName>
              <GroupCheckpoints>
                {items?.map((item) => (
                  <CheckpointLine
                    key={item.new_session_id}
                    className={item.isSelected ? `selected` : ``}
                    onClick={() => !item.isSelected && onRestoreSession?.(item)}
                  >
                    <CheckpointName>
                      {capitalize(item.input, true)}
                    </CheckpointName>
                    <CheckpointTime>
                      {item.date.toLocaleTimeString([], {
                        hour: `2-digit`,
                        minute: `2-digit`,
                      })}
                    </CheckpointTime>
                  </CheckpointLine>
                ))}
              </GroupCheckpoints>
            </Group>
          ))}
        </List>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  background: white;

  display: flex;
  flex-direction: column;
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid #dedede;

  min-height: 10rem;
  min-width: 41rem;

  font-family: 'Eesti', serif;
  box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &&.loading {
    align-items: center;
    justify-content: center;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  max-height: 50rem;
  overflow-y: scroll;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const GroupName = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
`;

const GroupCheckpoints = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-left: 0.3rem;
`;

const CheckpointLine = styled.div`
  font-size: 1.25rem;
  color: black;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem;
  border-radius: 0.4rem;
  gap: 1rem;

  cursor: pointer;

  &&:hover {
    background: #f5f5f5;
  }

  &&.selected {
    color: rgb(81, 85, 150);
    background: rgba(137, 192, 221, 0.151) !important;
    && > * {
      font-weight: bold !important;
    }
  }
`;

const CheckpointName = styled.span`
  font-weight: 500;
  max-width: 25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CheckpointTime = styled.span`
  font-size: 1.1rem;
`;

function formatDateWithOrdinal(date: Date): string {
  const day = date.getDate();
  const ordinal =
    // eslint-disable-next-line no-nested-ternary
    day % 10 === 1 && day !== 11
      ? `st`
      : // eslint-disable-next-line no-nested-ternary
      day % 10 === 2 && day !== 12
      ? `nd`
      : day % 10 === 3 && day !== 13
      ? `rd`
      : `th`;
  const month = date.toLocaleString(`en-US`, { month: `long` });
  const year = date.getFullYear();
  return `${month} ${day}${ordinal}, ${year}`;
}
